import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import { newsCategoryOptions } from "@/mocks/categories";

const STEP = 6;

export default function NewsList() {
  const [category, setCategory] = useState("全部");
  const [visible, setVisible] = useState(STEP);

  usePageMeta({
    title: "58动漫 - 最新动漫资讯",
    description:
      "58动漫资讯频道每日更新动漫行业动态、作品资讯、活动公告与展会信息，为你梳理最新最全的动漫圈新闻。",
    keywords: "动漫资讯,动画新闻,行业动态,动漫活动,展会信息,58动漫",
  });

  const sorted = useMemo(() => [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)), []);

  const filtered = useMemo(
    () => (category === "全部" ? sorted : sorted.filter((item) => item.category === category)),
    [sorted, category],
  );

  const list = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const hot = sorted.slice(0, 5);

  const handleCategory = (value: string) => {
    setCategory(value);
    setVisible(STEP);
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8 xl:px-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯" }]} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">最新动漫资讯</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
          汇集动漫行业动态、作品资讯、活动公告与展会信息，每日更新，助你不错过动漫圈的每一件大事。
        </p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {newsCategoryOptions.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => handleCategory(item)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              item === category
                ? "bg-primary-500 text-background-50"
                : "border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-6">
        <div className="lg:col-span-2">
          {list.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {list.map((news) => (
                  <NewsCard key={news.id} news={news} />
                ))}
              </div>

              <div className="mt-8 flex justify-center">
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + STEP)}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-arrow-down-double-line text-base" />
                    加载更多资讯
                  </button>
                ) : (
                  <span className="text-xs text-foreground-400">—— 已加载全部资讯 ——</span>
                )}
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-newspaper-line"
              title="该分类下暂无资讯"
              description="换个分类看看，或查看全部最新资讯。"
              actionLabel="查看全部资讯"
              onAction={() => handleCategory("全部")}
            />
          )}
        </div>

        <aside className="flex flex-col gap-6">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 font-heading text-base font-extrabold text-foreground-950">热门资讯</h2>
            <ol className="flex flex-col gap-3">
              {hot.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/news/detail/${item.id}`}
                    className="group flex items-start gap-3 transition-colors"
                  >
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                        index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="line-clamp-2 text-sm leading-snug text-foreground-700 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-lg bg-primary-50 p-5">
            <h2 className="font-heading text-base font-extrabold text-primary-700">订阅动漫资讯</h2>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              想第一时间了解动漫圈的新鲜事？去逛逛作品库，或把你喜欢的作品加入收藏。
            </p>
            <Link
              to="/category/最新"
              className="mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-compass-3-line text-sm" />
              浏览最新作品
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
}